/**
 * The live feed.
 *
 * One `EventSource` for the whole tab, shared by every page. Before this, each
 * view polled on its own timer: a deploy could sit finished for ten seconds
 * before the screen admitted it, and an idle dashboard still cost a request
 * every few seconds. Now the control plane pushes, and polling survives only as
 * a slow safety net for the case where the stream is down.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { API_URL, api, tokens } from './api'
import { useAuth } from './useAuth'

export interface StateEvent {
  kind: 'deploy' | 'service' | 'database'
  owner_id: string
  id: string
  status: string
  service_id: string | null
  name: string | null
  message: string | null
  ts: string
}

type Listener = (event: StateEvent) => void

interface LiveState {
  /** True while the stream is open — the UI shows this, so it must be honest. */
  connected: boolean
  subscribe: (listener: Listener) => () => void
}

const LiveContext = createContext<LiveState | null>(null)

/** Backoff between reconnects, in milliseconds. Capped so a long outage does
 *  not leave a tab silent for minutes after the server comes back. */
const BACKOFF = [1_000, 2_000, 4_000, 8_000, 15_000]

export function EventsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const [connected, setConnected] = useState(false)

  const listeners = useRef(new Set<Listener>())
  const subscribe = useCallback((listener: Listener) => {
    listeners.current.add(listener)
    return () => {
      listeners.current.delete(listener)
    }
  }, [])

  useEffect(() => {
    if (!user) {
      setConnected(false)
      return
    }

    let source: EventSource | null = null
    let retryTimer: number | undefined
    let attempt = 0
    let stopped = false

    const open = () => {
      if (!tokens.access()) return
      // The URL is what Traefik writes to its access log, so it carries a
      // single-use ticket rather than the access token itself.
      void api.streamTicket().then(
        ({ ticket }) => {
          if (stopped) return
          connect(ticket)
        },
        () => {
          // No ticket means no session, or the API is down: same backoff as
          // a dropped stream, and the refresh on the way back covers both.
          if (!stopped) retryLater()
        },
      )
    }

    const retryLater = () => {
      setConnected(false)
      const wait = BACKOFF[Math.min(attempt, BACKOFF.length - 1)]
      attempt += 1
      retryTimer = window.setTimeout(() => {
        // streamTicket already renews an expired session on 401. A dropped
        // stream is not evidence that a valid refresh token needs rotating.
        if (!stopped) open()
      }, wait)
    }

    const connect = (ticket: string) => {
      source = new EventSource(
        `${API_URL}/v1/events?ticket=${encodeURIComponent(ticket)}`,
      )

      source.addEventListener('ready', () => {
        attempt = 0
        setConnected(true)
      })

      source.addEventListener('state', (message) => {
        try {
          const event = JSON.parse((message as MessageEvent).data) as StateEvent
          // A throwing listener must not take the rest of the page down with it.
          for (const listener of [...listeners.current]) {
            try {
              listener(event)
            } catch (error) {
              console.error('live event listener failed', error)
            }
          }
        } catch {
          /* a malformed frame is not worth dropping the connection over */
        }
      })

      source.onerror = () => {
        // EventSource hides the status code, so an expired session looks
        // exactly like a dropped connection. Refreshing first covers both,
        // and costs one request per reconnect at most.
        source?.close()
        source = null
        if (stopped) return
        retryLater()
      }
    }

    // Browsers throttle timers in background tabs, so a stream that dropped
    // while hidden should reconnect the moment someone looks at it again.
    const onVisible = () => {
      if (document.visibilityState === 'visible' && !source && !stopped) {
        window.clearTimeout(retryTimer)
        attempt = 0
        open()
      }
    }
    document.addEventListener('visibilitychange', onVisible)

    open()

    return () => {
      stopped = true
      document.removeEventListener('visibilitychange', onVisible)
      window.clearTimeout(retryTimer)
      source?.close()
      setConnected(false)
    }
  }, [user])

  const value = useMemo<LiveState>(() => ({ connected, subscribe }), [connected, subscribe])

  return <LiveContext.Provider value={value}>{children}</LiveContext.Provider>
}

function useLive(): LiveState {
  const context = useContext(LiveContext)
  if (!context) throw new Error('live hooks must be used inside <EventsProvider>')
  return context
}

/** Whether the dashboard is currently receiving pushes. */
export function useLiveConnection(): boolean {
  return useLive().connected
}

/**
 * Run `handler` for every state change belonging to this account.
 *
 * The handler is held in a ref, so callers can pass an inline arrow function
 * without re-subscribing on every render.
 */
export function useStateEvents(handler: Listener): void {
  const { subscribe } = useLive()
  const ref = useRef(handler)
  ref.current = handler
  useEffect(() => subscribe((event) => ref.current(event)), [subscribe])
}

/**
 * Refresh a view when a matching event arrives.
 *
 * Coalesced: a deploy that publishes four transitions in a second triggers one
 * refetch, not four. `match` decides which events are relevant to this view.
 */
export function useLiveRefresh(
  reload: () => void,
  match: (event: StateEvent) => boolean,
  delay = 250,
): void {
  const reloadRef = useRef(reload)
  reloadRef.current = reload
  const matchRef = useRef(match)
  matchRef.current = match

  const timer = useRef<number>()
  useEffect(() => () => window.clearTimeout(timer.current), [])

  useStateEvents((event) => {
    if (!matchRef.current(event)) return
    window.clearTimeout(timer.current)
    timer.current = window.setTimeout(() => reloadRef.current(), delay)
  })
}
